/**
 * DEV-ONLY footprint authoring tool, exposed as `window.__fpTool`.
 *
 * Workflow (from the browser console / a headless driver):
 *   __fpTool.items()                 → placed furniture in this room
 *   __fpTool.show(furnitureId)       → focuses the item, numbers every floor tile
 *                                      that lies under its opaque pixels (1, 2, 3 …)
 *   __fpTool.select([3, 4, 7, 8])    → highlights the chosen tiles (the item's base)
 *   __fpTool.save()                  → writes them as the catalog footprint
 *                                      (same admin endpoint as the in-game tile panel)
 *   __fpTool.clear()                 → removes the overlay
 *
 * Tiles are numbered so a human (or an agent reading a screenshot) can pick the
 * base by number instead of guessing coordinates. Never shipped: installed only
 * under import.meta.env.DEV.
 */
import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import { TILE_HEIGHT, TILE_WIDTH } from '../../../domain/grid';
import { getFurnitureDepthComponents } from '../../../domain/depthMath';
import { getTileDims } from '../../../domain/tileMath';
import { updateFurnitureCatalogTiles } from '../furnitureApi';
import useInteriorHistoryStore from '../../../stores/useInteriorHistoryStore';
import type { RoomSceneState } from '../state/roomSceneState';
import type { SceneNode } from '../../../domain/sceneNode';
import type { RenderFurniture } from '../../../domain/types';

interface Candidate {
  n: number;
  tx: number;
  ty: number;
  dx: number;
  dy: number;
}

interface ToolState {
  furnitureId: number | null;
  catalogId: number | null;
  mirrored: boolean;
  center: { tileX: number; tileY: number } | null;
  candidates: Candidate[];
  selected: Set<number>;
  layer: PIXI.Container | null;
}

const HTW = TILE_WIDTH / 2;
const HTH = TILE_HEIGHT / 2;

const tileCenter = (tx: number, ty: number) => ({ x: (tx - ty) * HTW, y: (tx + ty + 1) * HTH });

async function loadAlpha(src: string): Promise<{ w: number; h: number; data: Uint8ClampedArray }> {
  const img = new Image();
  img.src = src;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(img, 0, 0);
  return { w: canvas.width, h: canvas.height, data: ctx.getImageData(0, 0, canvas.width, canvas.height).data };
}

export function installFootprintTool(
  sceneRef: { current: RoomSceneState },
  getViewport: () => Viewport | null,
): () => void {
  const st: ToolState = {
    furnitureId: null, catalogId: null, mirrored: false, center: null,
    candidates: [], selected: new Set(), layer: null,
  };

  const markerOf = (furnitureId: number) => sceneRef.current.furniture.markers.get(furnitureId) as unknown as PIXI.Container | undefined;
  const dataOf = (marker: PIXI.Container) =>
    (marker.children[0] as unknown as { furnitureData?: RenderFurniture }).furnitureData;

  const clear = () => {
    st.layer?.destroy({ children: true });
    st.layer = null;
  };

  const redraw = (labels = true) => {
    const marker = st.furnitureId != null ? markerOf(st.furnitureId) : undefined;
    const parent = marker?.parent;
    if (!parent) return;
    clear();
    const layer = new PIXI.Container();
    layer.zIndex = 1e9;
    layer.eventMode = 'none';
    const g = new PIXI.Graphics();
    layer.addChild(g);
    for (const c of st.candidates) {
      const { x, y } = tileCenter(c.tx, c.ty);
      const on = st.selected.has(c.n);
      g.poly([x, y - HTH, x + HTW, y, x, y + HTH, x - HTW, y], true);
      if (on) g.fill({ color: 0xffa500, alpha: 0.55 });
      g.stroke({ width: on ? 0.9 : 0.35, color: on ? 0xff6a00 : 0x00e5ff, alpha: on ? 0.95 : 0.7 });
      if (labels) {
        const t = new PIXI.Text({
          text: String(c.n),
          style: { fontFamily: 'Arial', fontSize: 28, fontWeight: '700', fill: on ? 0x000000 : 0xffffff, stroke: { color: on ? 0xffffff : 0x000000, width: 4 } },
        });
        t.scale.set(0.13);
        t.alpha = 0.85;
        t.anchor.set(0.5);
        t.position.set(x, y);
        layer.addChild(t);
      }
    }
    parent.sortableChildren = true;
    parent.addChild(layer);
    st.layer = layer;
  };

  const tool = {
    items: () => Array.from(sceneRef.current.furniture.markers.entries()).map(([id, m]) => {
      const fd = dataOf(m as unknown as PIXI.Container);
      return { id, catalogId: fd?.furniture_catalog_id, category: fd?.furniture_catalog?.category, footprint: fd?.furniture_catalog?.footprint_tiles ?? null };
    }),

    /** Number every tile under the item's opaque pixels; optionally zoom to it. */
    show: async (furnitureId: number, opts: { zoom?: number; fillBox?: boolean } = {}) => {
      const marker = markerOf(furnitureId);
      if (!marker) throw new Error(`furniture ${furnitureId} not in this room`);
      const sprite = marker.children[0] as PIXI.Sprite;
      const fd = dataOf(marker);
      if (!fd) throw new Error('no furnitureData');
      const { w, h, isRug } = getTileDims(fd.furniture_catalog);
      const depth = getFurnitureDepthComponents(marker as unknown as SceneNode, w, h, isRug);
      st.furnitureId = furnitureId;
      st.catalogId = fd.furniture_catalog_id;
      st.mirrored = sprite.scale.x < 0;
      st.center = { tileX: Math.round(depth.tileX), tileY: Math.round(depth.tileY) };
      const alpha = await loadAlpha(`/game-assets/interior/${fd.furniture_catalog_id}.webp`);
      const parent = marker.parent!;
      const opaqueAt = (rx: number, ry: number) => {
        const local = sprite.toLocal(parent.toGlobal(new PIXI.Point(rx, ry)));
        const tw = sprite.texture.width, th = sprite.texture.height;
        const u = (local.x + sprite.anchor.x * tw) / tw;
        const v = (local.y + sprite.anchor.y * th) / th;
        if (u < 0 || v < 0 || u >= 1 || v >= 1) return false;
        const px = Math.floor(u * alpha.w), py = Math.floor(v * alpha.h);
        return alpha.data[(py * alpha.w + px) * 4 + 3] > 40;
      };
      const found: Omit<Candidate, 'n'>[] = [];
      const c = st.center;
      for (let tx = c.tileX - 30; tx <= c.tileX + 30; tx++) {
        for (let ty = c.tileY - 30; ty <= c.tileY + 30; ty++) {
          if (tx < 0 || ty < 0 || tx >= 40 || ty >= 40) continue;
          const { x, y } = tileCenter(tx, ty);
          const pts = [[0, 0], [0.45, 0], [-0.45, 0], [0, 0.45], [0, -0.45]];
          if (pts.some(([a, b]) => opaqueAt(x + a * HTW, y + b * HTH))) {
            found.push({ tx, ty, dx: tx - c.tileX, dy: ty - c.tileY });
          }
        }
      }
      // Also number the floor tiles inside the item's tile bounding box that no pixel
      // covers (e.g. the open floor between a piano's front legs): a footprint is a
      // ground plan and may include them.
      if (found.length && opts.fillBox !== false) {
        const key = (dx: number, dy: number) => `${dx},${dy}`;
        const have = new Set(found.map(f => key(f.dx, f.dy)));
        const dxs = found.map(f => f.dx), dys = found.map(f => f.dy);
        for (let dx = Math.min(...dxs); dx <= Math.max(...dxs); dx++) {
          for (let dy = Math.min(...dys); dy <= Math.max(...dys); dy++) {
            const tx = c.tileX + dx, ty = c.tileY + dy;
            if (tx < 0 || ty < 0 || tx >= 40 || ty >= 40 || have.has(key(dx, dy))) continue;
            found.push({ tx, ty, dx, dy });
          }
        }
      }
      // Reading order on screen: top to bottom, left to right.
      found.sort((p, q) => {
        const a = tileCenter(p.tx, p.ty), b = tileCenter(q.tx, q.ty);
        return a.y - b.y || a.x - b.x;
      });
      st.candidates = found.map((f, i) => ({ ...f, n: i + 1 }));
      // Pre-select the current footprint, if any.
      const fp = fd.furniture_catalog.footprint_tiles ?? [];
      st.selected = new Set(st.candidates.filter(cd => fp.some(([dx, dy]) => {
        const [vx, vy] = st.mirrored ? [dy, dx] : [dx, dy];
        return vx === cd.dx && vy === cd.dy;
      })).map(cd => cd.n));
      const vp = getViewport();
      if (vp) {
        // Centre in world coords first: getBounds() is in screen space and changes with zoom.
        const b = marker.getBounds();
        const worldCenter = vp.toWorld(b.x + b.width / 2, b.y + b.height / 2);
        const worldH = b.height / vp.scale.y;
        const worldW = b.width / vp.scale.x;
        const fit = Math.min((vp.screenHeight * 0.85) / Math.max(1, worldH), (vp.screenWidth * 0.85) / Math.max(1, worldW));
        vp.setZoom(opts.zoom ?? fit, true);
        vp.moveCenter(worldCenter);
      }
      redraw(true);
      return { center: st.center, mirrored: st.mirrored, count: st.candidates.length };
    },

    /** Camera state (world coords) — for debugging pans. */
    camera: () => {
      const vp = getViewport();
      return vp ? { cx: vp.center.x, cy: vp.center.y, scale: vp.scale.y, sw: vp.screenWidth, sh: vp.screenHeight } : null;
    },

    /** Undo history as recorded (cursor, size, each revision's try-on entries) — for debugging. */
    history: () => {
      const h = useInteriorHistoryStore.getState();
      return {
        cursor: h.cursor,
        size: h.revisions.length,
        previews: h.revisions.map((r) => r.filter((i) => i.id < 0).map((i) => [i.id, Math.round(i.x ?? 0), Math.round(i.y ?? 0), i.placement_mode, i.is_mirrored])),
      };
    },

    /** Screen (CSS px) bounds of a placed item — where to put a finger in a headless test. */
    bounds: (furnitureId: number) => {
      const b = markerOf(furnitureId)?.getBounds();
      return b ? { x: b.x, y: b.y, width: b.width, height: b.height } : null;
    },

    /** Wall smart guides on screen right now (and their key: v<height> / u<side><pos>). */
    guides: () => ({
      visible: !!sceneRef.current.overlays.wallGuides?.visible,
      key: sceneRef.current.drag.state?.wallGuideKey ?? '',
    }),

    /** Room-local geometry of a wall item: marker, sprite top/bottom, wall offset, wall sprites. */
    wallInfo: (furnitureId: number) => {
      const marker = markerOf(furnitureId);
      const parent = marker?.parent;
      if (!marker || !parent) return null;
      const sb = (marker.children[0] as PIXI.Container | undefined)?.getBounds();
      const toLocal = (x: number, y: number) => parent.toLocal(new PIXI.Point(x, y));
      const top = sb ? toLocal(sb.x, sb.y) : null;
      const bottom = sb ? toLocal(sb.x + sb.width, sb.y + sb.height) : null;
      const { wallLeft, wallRight } = sceneRef.current.sprites;
      const wl = (s: PIXI.Container | null | undefined) => {
        if (!s) return null;
        const b = s.getBounds();
        const a = toLocal(b.x, b.y), z = toLocal(b.x + b.width, b.y + b.height);
        return { x0: a.x, y0: a.y, x1: z.x, y1: z.y };
      };
      const fd = (marker as unknown as { furnitureData?: { wall_floor_offset?: number | null } }).furnitureData;
      return {
        marker: { x: marker.position.x, y: marker.position.y },
        sprite: top && bottom ? { x0: top.x, y0: top.y, x1: bottom.x, y1: bottom.y } : null,
        wallFloorOffset: fd?.wall_floor_offset ?? null,
        red: (marker.children[0] as PIXI.Sprite | undefined)?.tint === 0xFF4444,
        wallLeft: wl(wallLeft), wallRight: wl(wallRight),
      };
    },

    /** Screen (CSS px) position of every numbered tile's centre — to match a screenshot. */
    layout: () => {
      const marker = st.furnitureId != null ? markerOf(st.furnitureId) : undefined;
      const parent = marker?.parent;
      if (!parent) return [];
      return st.candidates.map(c => {
        const { x, y } = tileCenter(c.tx, c.ty);
        const p = parent.toGlobal(new PIXI.Point(x, y));
        return { n: c.n, x: Math.round(p.x), y: Math.round(p.y) };
      });
    },

    /** "X-ray": make the shown item semi-transparent (1 = normal) to see the floor under it. */
    xray: (alpha = 0.35) => {
      const marker = st.furnitureId != null ? markerOf(st.furnitureId) : undefined;
      if (marker) (marker.children[0] as PIXI.Sprite).alpha = alpha;
      return alpha;
    },

    /** Numbered tiles with their offsets from the item's centre tile (as drawn). */
    candidates: () => st.candidates.map(({ n, dx, dy }) => ({ n, dx, dy })),

    /** Select every numbered tile inside an offset rectangle (inclusive) — footprints are grid rectangles. */
    selectRect: (dxMin: number, dxMax: number, dyMin: number, dyMax: number, opts: { labels?: boolean } = {}) =>
      tool.select(st.candidates
        .filter(c => c.dx >= dxMin && c.dx <= dxMax && c.dy >= dyMin && c.dy <= dyMax)
        .map(c => c.n), opts),

    select: (numbers: number[], opts: { labels?: boolean } = {}) => {
      st.selected = new Set(numbers.filter(n => st.candidates.some(c => c.n === n)));
      redraw(opts.labels ?? true);
      return Array.from(st.selected).sort((a, b) => a - b);
    },

    /** Footprint (canonical, un-mirrored offsets) that save() would write. */
    tiles: (): [number, number][] => st.candidates
      .filter(c => st.selected.has(c.n))
      .map(c => (st.mirrored ? [c.dy, c.dx] : [c.dx, c.dy]) as [number, number]),

    save: async () => {
      if (st.catalogId == null) throw new Error('nothing shown');
      const tiles = tool.tiles();
      await updateFurnitureCatalogTiles(st.catalogId, tiles.length ? tiles : null, null, tiles.length === 0, false);
      return { catalogId: st.catalogId, tiles };
    },

    clear,
  };

  (window as unknown as Record<string, unknown>).__fpTool = tool;
  return () => {
    clear();
    delete (window as unknown as Record<string, unknown>).__fpTool;
  };
}
